import { Box, Card, Flex, Heading, Text, TextField, Button, Callout } from "@radix-ui/themes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useState } from "react";
import { CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { forgotPasswordSchema, type ForgotPasswordInput } from "@/lib/schemas";
import { forgotPassword } from "@/lib/api";
import { FieldError } from "@/components/ui/field-error";
import { LanguageSwitcher } from "@/components/ui/language-switcher";

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  async function onSubmit(data: ForgotPasswordInput) {
    const result = await forgotPassword(data.email);
    if (!result.ok) {
      toast.error(result.error ?? t("common.error_generic"));
      return;
    }
    setSent(true);
  }

  return (
    <Flex
      align="center"
      justify="center"
      style={{ minHeight: "100vh", backgroundColor: "var(--gray-2)" }}
    >
      <Box style={{ width: "100%", maxWidth: "400px" }} p="4">
        <Flex direction="column" align="center" gap="1" mb="6">
          <Heading size="6" weight="bold">
            {t("common.app_name")}
          </Heading>
          <Text size="2" color="gray">
            {t("auth.forgot_reset_subtitle")}
          </Text>
        </Flex>

        <Card size="3">
          {sent ? (
            <Callout.Root color="green" variant="soft">
              <Callout.Icon>
                <CheckCircle size={16} />
              </Callout.Icon>
              <Callout.Text>
                {t("auth.forgot_success_callout")}
              </Callout.Text>
            </Callout.Root>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray" as="p">
                  {t("auth.forgot_subtitle")}
                </Text>

                <Flex direction="column" gap="1">
                  <Text as="label" size="2" weight="medium" htmlFor="email">
                    {t("auth.email_label")}
                  </Text>
                  <TextField.Root
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    autoComplete="email"
                    size="3"
                    {...register("email")}
                    color={errors.email ? "red" : undefined}
                  />
                  <FieldError message={errors.email?.message} />
                </Flex>

                <Button type="submit" size="3" style={{ width: "100%" }} disabled={isSubmitting}>
                  {isSubmitting ? t("auth.forgot_pending") : t("auth.forgot_button")}
                </Button>
              </Flex>
            </form>
          )}

          <Flex justify="center" mt="4">
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Text size="2" color="violet">
                ← {t("auth.forgot_back")}
              </Text>
            </Link>
          </Flex>
        </Card>

        {/* Sélecteur de langue discret */}
        <Flex justify="center" mt="4">
          <LanguageSwitcher />
        </Flex>
      </Box>
    </Flex>
  );
}
